import { useState } from 'react';

interface ClassSlot {
  name: string;
  coach: string;
  time: string;
  duration: number;
  color: string;
}

type ScheduleData = Record<string, ClassSlot[]>;

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const colorMap = {
  crossfit:     'bg-orange-500/20 border-orange-500/50 text-orange-300',
  calisthenics: 'bg-teal-500/20 border-teal-500/50 text-teal-300',
  mobility:     'bg-green-500/20 border-green-500/50 text-green-300',
  kids:         'bg-purple-500/20 border-purple-500/50 text-purple-300',
};

const schedule: ScheduleData = {
  Monday: [
    { name: 'CrossFit',                  coach: 'Charbel Farhat',                   time: '08:00', duration: 60,  color: colorMap.crossfit },
    { name: 'Calisthenics (Beginner)',   coach: 'Gabriel Harika',                   time: '18:00', duration: 90,  color: colorMap.calisthenics },
    { name: 'Calisthenics (Adv / Int)', coach: 'Gabriel Harika',                   time: '19:30', duration: 90,  color: colorMap.calisthenics },
  ],
  Tuesday: [
    { name: 'Kids Class',               coach: 'Gabriel Harika & Victor Matar',   time: '16:30', duration: 60,  color: colorMap.kids },
    { name: 'Mobility',                 coach: 'Myriam Chami',                     time: '18:30', duration: 60,  color: colorMap.mobility },
  ],
  Wednesday: [
    { name: 'CrossFit',                 coach: 'Charbel Farhat',                   time: '08:00', duration: 60,  color: colorMap.crossfit },
    { name: 'Kids Class',               coach: 'Gabriel Harika & Victor Matar',   time: '17:00', duration: 60,  color: colorMap.kids },
    { name: 'Calisthenics (Beginner)',  coach: 'Gabriel Harika',                   time: '18:00', duration: 90,  color: colorMap.calisthenics },
    { name: 'Calisthenics (Adv / Int)', coach: 'Gabriel Harika',                   time: '19:30', duration: 90,  color: colorMap.calisthenics },
  ],
  Thursday: [
    { name: 'Kids Class',               coach: 'Gabriel Harika & Victor Matar',   time: '16:30', duration: 60,  color: colorMap.kids },
  ],
  Friday: [
    { name: 'CrossFit',                 coach: 'Charbel Farhat',                   time: '08:00', duration: 60,  color: colorMap.crossfit },
    { name: 'Kids Class',               coach: 'Gabriel Harika & Victor Matar',   time: '17:00', duration: 60,  color: colorMap.kids },
    { name: 'Calisthenics (Beginner)',  coach: 'Gabriel Harika',                   time: '18:00', duration: 90,  color: colorMap.calisthenics },
    { name: 'Calisthenics (Adv / Int)', coach: 'Gabriel Harika',                   time: '19:30', duration: 90,  color: colorMap.calisthenics },
  ],
  Saturday: [
    { name: 'Mobility',                 coach: 'Myriam Chami',                     time: '11:00', duration: 60,  color: colorMap.mobility },
  ],
};

const legend = [
  { label: 'CrossFit',    color: 'bg-orange-500' },
  { label: 'Calisthenics', color: 'bg-teal-500' },
  { label: 'Mobility',    color: 'bg-green-500' },
  { label: 'Kids Class',  color: 'bg-purple-500' },
];

const PX_PER_MIN    = 2.0;
const TIME_COL_W    = 72;
const V_PAD         = 20;
const GAP_THRESHOLD = 90;

function timeToMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minsToLabel(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

function groupSegments(classes: ClassSlot[]): ClassSlot[][] {
  if (!classes.length) return [];
  const sorted = [...classes].sort((a, b) => timeToMins(a.time) - timeToMins(b.time));
  const groups: ClassSlot[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const gap  = timeToMins(sorted[i].time) - (timeToMins(prev.time) + prev.duration);
    if (gap > GAP_THRESHOLD) groups.push([]);
    groups[groups.length - 1].push(sorted[i]);
  }
  return groups;
}

function TimeSegment({ classes }: { classes: ClassSlot[] }) {
  const sorted     = [...classes].sort((a, b) => timeToMins(a.time) - timeToMins(b.time));
  const gridStart  = Math.floor(timeToMins(sorted[0].time) / 60) * 60;
  const rawEnd     = timeToMins(sorted[sorted.length - 1].time) + sorted[sorted.length - 1].duration;
  const gridEnd    = Math.ceil(rawEnd / 60) * 60;
  const hourMarks: number[] = [];
  for (let t = gridStart; t <= gridEnd; t += 60) hourMarks.push(t);
  const containerH = V_PAD * 2 + Math.round((gridEnd - gridStart) * PX_PER_MIN);
  const yOf = (mins: number) => V_PAD + Math.round((mins - gridStart) * PX_PER_MIN);

  return (
    <div className="relative" style={{ height: containerH }}>
      {hourMarks.map((t) => (
        <div key={t} className="absolute left-0 right-0 h-0 pointer-events-none" style={{ top: yOf(t) }}>
          <span
            className="absolute text-gray-600 text-xs font-mono text-right pr-3 -translate-y-1/2 select-none"
            style={{ width: TIME_COL_W, top: 0, left: 0 }}
          >
            {minsToLabel(t)}
          </span>
          <div className="absolute border-t border-white/[0.06]" style={{ left: TIME_COL_W, right: 0, top: 0 }} />
        </div>
      ))}

      {sorted.map((cls) => {
        const endTime = minsToLabel(timeToMins(cls.time) + cls.duration);
        const boxH    = Math.round(cls.duration * PX_PER_MIN);
        return (
          <div
            key={cls.name + cls.time}
            className={`absolute z-10 border rounded-xl px-4 flex flex-col justify-center gap-1.5 ${cls.color}`}
            style={{
              left:   TIME_COL_W + 8,
              right:  0,
              top:    yOf(timeToMins(cls.time)) + 1,
              height: boxH - 2,
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm leading-tight">{cls.name}</span>
              <span className="text-xs font-mono opacity-90 flex-shrink-0 bg-black/25 rounded px-1.5 py-0.5">
                {cls.time} – {endTime}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs opacity-70">
              <i className="ri-user-line text-xs" />
              <span className="truncate">{cls.coach}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function WeeklySchedule() {
  const [activeDay, setActiveDay] = useState('Monday');
  const segments = groupSegments(schedule[activeDay]);

  return (
    <section id="schedule" className="bg-[#0a0a0a] py-16 lg:py-32 px-4 lg:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Header row — title left, legend right */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-12 lg:mb-16">
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
              <span className="w-4 h-px bg-[#14B8A6]" />
              TIMETABLE
            </span>
            <h2 className="text-white text-3xl sm:text-4xl lg:text-6xl font-bold mb-3">Weekly Schedule</h2>
            <p className="text-gray-400 text-base lg:text-lg max-w-xl leading-relaxed">
              Plan your week and never miss a session.
            </p>
          </div>

          {/* Legend — visible on lg+, shows above timeline on mobile */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 lg:flex-col lg:gap-y-2.5 lg:items-end lg:pt-2">
            {legend.map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.color}`} />
                <span className="text-gray-400 text-xs">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day Selector */}
        <div className="flex overflow-x-auto gap-2 mb-6 pb-2 scrollbar-hide">
          {days.map((day) => (
            <button
              key={day}
              onClick={() => setActiveDay(day)}
              className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer flex-shrink-0 ${
                activeDay === day
                  ? 'bg-[#14B8A6] text-white scale-105'
                  : 'bg-[#1a1a1a] text-gray-400 hover:text-white hover:bg-[#252525] border border-white/[0.06]'
              }`}
            >
              {day}
            </button>
          ))}
        </div>

        {/* Timeline */}
        <div className="bg-[#111111] rounded-3xl border border-white/[0.06] px-4 sm:px-6 overflow-hidden">
          {segments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-calendar-check-line text-3xl text-gray-600 mb-3" />
              <p className="text-gray-500 text-sm">No classes scheduled for {activeDay}.</p>
              <p className="text-gray-600 text-xs mt-1">Open gym available during regular hours.</p>
            </div>
          ) : (
            segments.map((segment, i) => (
              <div key={i}>
                {i > 0 && (
                  <div className="flex items-center gap-3 py-2" style={{ paddingLeft: TIME_COL_W + 4 }}>
                    <div className="flex-1 border-t border-dashed border-white/10" />
                    <span className="text-gray-600 text-xs italic flex-shrink-0">Open Gym</span>
                    <div className="flex-1 border-t border-dashed border-white/10" />
                  </div>
                )}
                <TimeSegment classes={segment} />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
